import * as XLSX from 'xlsx';
import { toast } from "sonner";
import { formatCurrency, formatDate, calculatePriceToCustomer, calculateAdjustedBufferAmount, calculateTotalWithAdjustedBuffer, calculateAverageCostPerTask, calculateOverallBlendedRate } from "./costCalculations";

export const handleDownloadCostAnalysis = (
  costAnalysis: any,
  selectedProject: any,
  taskProfitability: any[],
  profitMargin: number[],
  bufferPercent: number[],
  calculatedTotalCost?: number,
  resourceAssignments?: any[]
) => {
  if (!selectedProject) {
    toast.error('No project selected');
    return;
  }

  if (taskProfitability.length === 0) {
    toast.error('No task profitability data to download');
    return;
  }

  try {
    const workbook = XLSX.utils.book_new();

    // Use calculated total cost if available, otherwise fall back to costAnalysis total_cost
    const actualTotalCost = calculatedTotalCost || costAnalysis?.total_cost || 0;

    if (actualTotalCost === 0) {
      toast.error('No cost data available to download');
      return;
    }

    // Calculate overall blended rate using resource assignments if available
    const overallBlendedRate = resourceAssignments && resourceAssignments.length > 0
      ? calculateOverallBlendedRate(resourceAssignments, profitMargin[0], bufferPercent[0])
      : 0;

    // First tab: Overall Project Margin Projections
    const marginData = [
      ['Metric', 'Value'],
      ['Projected Price to Customer', formatCurrency(calculatePriceToCustomer(actualTotalCost, profitMargin[0], bufferPercent[0]))],
      ['Overall Blended Rate', formatCurrency(overallBlendedRate)],
      ['Profit Margin', `${profitMargin[0]}%`],
      ['Average Cost Per Project Task', formatCurrency(calculateAverageCostPerTask(actualTotalCost, selectedProject?.tasks?.length || 0))],
      ['Total Project Cost', formatCurrency(actualTotalCost)],
      ['Buffer Amount', formatCurrency(calculateAdjustedBufferAmount(actualTotalCost, bufferPercent[0]))],
      ['Buffer Percentage', `${bufferPercent[0]}%`],
      ['Total Project Cost (With Buffer)', formatCurrency(calculateTotalWithAdjustedBuffer(actualTotalCost, bufferPercent[0]))]
    ];

    const marginSheet = XLSX.utils.aoa_to_sheet(marginData);
    XLSX.utils.book_append_sheet(workbook, marginSheet, 'Project Margin Projections');

    // Second tab: Detailed Projections By Project Task
    if (taskProfitability.length > 0) {
      const taskHeaders = [
        'SN', 'Project Task', 'Start Date', 'End Date', 'Duration', 
        '# Resources', 'Total Resource Cost', 'Total Resource Cost (With Buffer)', 
        'Profit Margin', 'Projected Price to Customer', 'Blended Rate to Customer'
      ];

      const taskData = [
        taskHeaders,
        ...taskProfitability.map(task => [
          task.sn,
          task.taskName,
          formatDate(task.startDate),
          formatDate(task.endDate),
          `${task.duration} days`,
          task.numResources,
          formatCurrency(task.totalResourceCost),
          formatCurrency(task.withBuffer),
          `${task.profitMargin}%`,
          formatCurrency(task.priceToCustomer),
          formatCurrency(task.blendedRateToCustomer)
        ])
      ];

      const taskSheet = XLSX.utils.aoa_to_sheet(taskData);
      XLSX.utils.book_append_sheet(workbook, taskSheet, 'Task Projections');
    }

    // Download the file
    const fileName = `${selectedProject.name}_Cost_Analysis_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    toast.success('Cost analysis downloaded successfully');
  } catch (error) {
    console.error('Error downloading cost analysis:', error);
    toast.error('Failed to download cost analysis');
  }
};
